import {cn} from '@/lib/utils'
import {CircleDashedIcon, ThumbsDownIcon, ThumbsUpIcon} from 'lucide-react'
import {Card, CardContent} from '@/components/ui/card'
import {Table, TableBody, TableCell, TableRow} from '@/components/ui/table'
import {PrivateImg} from '@/components/private-img'
import {PhotoPlaceholder} from '@/components/photo-placeholder'
import {PhotoTimelapse} from '@/components/photo-timelapse'
import type {PhotoData} from '@/types'
import dayjs from 'dayjs'
import type {ComponentProps} from 'react'

export function PhotoCard({className, photo, ...props}: ComponentProps<'div'> & {photo: PhotoData}) {
  return (
    <Card className={cn('w-full md:w-86', className)} {...props}>
      <CardContent className='flex flex-col gap-6'>
        <div className='flex flex-row flex-wrap gap-2'>
          {photo.status >= '40_photo_taken' ? (
            <PrivateImg className='max-h-45 max-w-36' src={`/api/photos/${photo.id}/files/photo.jpg`} />
          ) : (photo.timelapse_frames || 0) > 0 ? (
            <PhotoTimelapse className='max-h-45 max-w-36' photoId={photo.id} frameCount={photo.timelapse_frames!} interval={1000} />
          ) : (
            <PhotoPlaceholder className='grow-1 h-45' />
          )}
          {photo.share_url ? (
            <a href={photo.share_url} target='_blank'>
              <PrivateImg
                className='max-h-45 max-w-36'
                alt={photo.share_url.replace(/^.+\//, '')}
                src={`/api/photos/${photo.id}/files/generated.jpg`}
              />
            </a>
          ) : (
            <PhotoPlaceholder className='grow-1 h-45' />
          )}
        </div>
        <div>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>ID (Session ID)</TableCell>
                <TableCell>
                  #{photo.id} (session {photo.session_id})
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Created At</TableCell>
                <TableCell>{dayjs(photo.created_at + 'Z').format('YYYY-MM-DD HH:mm:ss')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Booth</TableCell>
                <TableCell>{photo.booth}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>{photo.status}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Templates Viewed</TableCell>
                <TableCell>{photo.templates_viewed}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Template Name</TableCell>
                <TableCell>{photo.template_name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Candidates Viewed</TableCell>
                <TableCell>{photo.candidates_viewed}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Reshoots</TableCell>
                <TableCell>{photo.reshoots}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Regenerations</TableCell>
                <TableCell>{photo.regenerations}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Results Viewed</TableCell>
                <TableCell>{photo.results_viewed}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Rating</TableCell>
                <TableCell>
                  {photo.rating > 0 && <ThumbsUpIcon size={16} />}
                  {photo.rating < 0 && <ThumbsDownIcon size={16} />}
                  {photo.rating === 0 && <CircleDashedIcon size={16} />}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
